<template>
  <section class="schedule-card" aria-labelledby="schedule-title" :aria-busy="loading">
    <header class="schedule-toolbar">
      <div><h1 id="schedule-title">Ders Takvimi</h1><p role="status">{{ loading ? 'Ders takvimi yükleniyor…' : loadError ? 'Ders takvimi yüklenemedi' : selectedMonthText + ' ayı · ' + lessons.length + ' ders' }}</p></div>
      <div class="month-switcher" aria-label="Ay seçimi">
        <button type="button" aria-label="Önceki ay" :disabled="busy" @click="prevMonth"><i class="bi bi-chevron-left" aria-hidden="true"></i></button>
        <span>{{ selectedMonthText }} {{ selectedYear }}</span>
        <button type="button" aria-label="Sonraki ay" :disabled="busy" @click="nextMonth"><i class="bi bi-chevron-right" aria-hidden="true"></i></button>
      </div>
      <div class="view-switcher" role="group" aria-label="Takvim görünümü">
        <button type="button" :aria-pressed="view === 'agenda'" @click="view = 'agenda'">Ajanda (Dersler)</button>
        <button type="button" :aria-pressed="view === 'calendar'" @click="view = 'calendar'">Aylık Takvim</button>
      </div>
    </header>
    <div v-if="loading" class="schedule-loading" role="status"><span class="visually-hidden">Dersler yükleniyor</span><div v-for="n in 3" :key="n" class="skeleton"></div></div>
    <div v-else-if="loadError" class="empty-state" role="alert"><h2>Ders takvimi yüklenemedi</h2><p>Lütfen tekrar deneyin.</p><button type="button" class="action primary" @click="init">Tekrar Dene</button></div>
    <template v-else>
      <div v-if="view === 'calendar'" class="calendar" aria-label="Aylık ders takvimi">
        <div v-for="day in ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz']" :key="day" class="weekday">{{ day }}</div>
        <div v-for="n in calendarOffset" :key="'blank-' + n" class="calendar-blank" aria-hidden="true"></div>
        <button v-for="day in dayInMonth" :key="day.date" type="button" class="calendar-day" :class="{ 'has-lessons': day.lessons.length, 'is-today': day.date === today }" :aria-pressed="selectedDate === day.date" :aria-label="day.dayNumber + ' ' + selectedMonthText + ', ' + day.lessons.length + ' ders'" :aria-current="day.date === today ? 'date' : undefined" @click="selectedDate = day.date">
          <span>{{ day.dayNumber }}</span><small v-if="day.lessons.length">{{ day.lessons.length }} ders</small>
        </button>
      </div>
      <div v-if="!lessons.length" class="empty-state compact"><p>Bu ay için henüz planlanmış bir dersiniz bulunmamaktadır.</p></div>
      <template v-else>
        <h2 v-if="view === 'calendar'" class="day-heading">{{ selectedDayLabel }}</h2>
        <div v-if="!visibleLessons.length" class="empty-state compact"><p>Bugün için planlanmış dersiniz bulunmuyor.</p></div>
        <div v-else ref="agendaList" class="agenda-list">
          <article v-for="d in visibleLessons" :key="d.key" :data-lesson-key="d.key" class="lesson-card" :class="{ 'is-today': d.date === today, 'is-completed': d.lessonStatus === 2 }">
            <div class="date-badge"><strong>{{ d.dayNumber }}</strong><span>{{ selectedMonthText.slice(0, 3) }}</span></div>
            <div class="lesson-info">
              <div class="lesson-tags"><span class="status" :class="{ 'status-next': d.key === nextLessonKey }">{{ d.key === nextLessonKey ? 'Sıradaki Ders' : statusLabel(d.lessonStatus) }}</span><span>{{ d.bookTitle }}</span></div>
              <h3>{{ [d.bookLevel, d.unitNumber, d.subjectTitle].filter(Boolean).join(' ') || 'İngilizce dersi' }}</h3>
              <div class="lesson-meta"><span><i class="bi bi-calendar3" aria-hidden="true"></i> {{ d.dayText }}</span><span v-if="d.time"><i class="bi bi-clock" aria-hidden="true"></i> {{ d.time }} (Türkiye saati)</span>
                <span v-for="resource in resources(d)" :key="resource.label" :title="resource.label"><i :class="resource.icon" aria-hidden="true"></i><span class="visually-hidden">{{ resource.label }}</span></span>
              </div>
            </div>
            <div class="lesson-actions">
              <button v-if="d.lessonStatus === 0" type="button" class="action secondary" :disabled="busy || d.canDeclareAbsence || d.isAbsenceDeclared" @click="OpenClosePostponeDialogModal(true, d)">Ertele</button>
              <span v-if="d.isAbsenceDeclared" class="absence-notice">Katılmayacağınız bildirildi</span>
              <button v-else-if="d.canDeclareAbsence" type="button" class="action absence" :disabled="busy" @click="OpenCloseAbsenceDialogModal(true, d)">Derse Katılmayacağım</button>
              <router-link v-if="d.bookId" :to="{ path: '/courses/' + CreateUrl(d.bookTitle) + '/' + d.bookId, query: calendarQuery }" class="action" :class="d.date === today ? 'primary' : 'secondary'">{{ d.lessonStatus === 2 && d.hasLessonNote ? 'Notları Gör' : 'Derse Git' }} <i class="bi bi-arrow-right" aria-hidden="true"></i></router-link>
            </div>
          </article>
        </div>
      </template>
    </template>
  </section>
    <PostponeDialog 
    :ShowCloseButton="false"
    :Title="postponeDialog.title"
    :Content="postponeDialogContent" 
    :Visible="postponeDialog.postponeDialogModalVisible" 
    @CloseModal="OpenClosePostponeDialogModal($event,null)"
    @CustomDialogAnswer="PostponeDialogAnswer($event)"
    />
    <AbsenceDialog
    ModalName="AbsenceDialog"
    :ShowCloseButton="false"
    :Title="absenceDialog.title"
    :Content="absenceDialog.content"
    :Visible="absenceDialog.visible"
    @CloseModal="OpenCloseAbsenceDialogModal($event,null)"
    @CustomDialogAnswer="AbsenceDialogAnswer($event)"
    />
</template>
  <script>
  import {mapActions } from "vuex";
  import { readScheduleState, scheduleQuery } from '@/common/schedule-navigation';
  import {DateFormat} from '@/common/datetime-helper';
  import{CleanUrl} from '@/common/url-helper';
  import { LoadingToast,UpdateToast,ToastType } from "@/common/notification.js";
  import PostponeDialog from "@/components/Modals/CustomDialogModal.vue";
  import AbsenceDialog from "@/components/Modals/CustomDialogModal.vue";
  export default {
    name: "TrainingCalender",
    props: {
      autoScrollToLesson: { type: Boolean, default: false }
    },
    components:{
      PostponeDialog,
      AbsenceDialog
    },
    data() {
      return {
        dayInMonth:[],
        lessons: [], loading: false, loadError: false,
        selectedMonthText:'',
        ...readScheduleState(this.$route.query),
        schedule:null,
        loadingButtonDisable:false,
        postponeDialog:{
          postponeDialogModalVisible:false,
          trainingFileId:'',
          lessonDate:'',
          title:'Uyarı',
          constContent:'<p>#LESSONDATE# tarihli dersinizi ertelemek istiyor musunuz?</p><strong>Not:</strong> Ders erteleme işlemini, ders saatinizden <strong class=text-danger><i>en az 2 saat önce</i></strong> yapabilirsiniz. Bunun dışında işlem <u>yapılmamaktadır.</u>',
        },
        absenceDialog:{
          visible:false,
          lesson:null,
          title:'Uyarı',
          content:'<p>Bu derse katılmayacağınızı öğretmeninize bildirmek istiyor musunuz?</p><strong class="text-danger">Bu işlem dersi ertelemez veya iptal etmez. Ders, ders hakkınızdan düşecektir. Öğretmeninize yalnızca katılamayacağınız bilgisi iletilir.</strong>'
        }
      }
    },
    computed: {
      calendarQuery() { return scheduleQuery(this); },
      busy() { return this.loading || this.loadingButtonDisable; },
      today() { return this.dateKey(new Date()); },
      calendarOffset() { return (new Date(this.selectedYear, this.selectedMonth, 1).getDay() + 6) % 7; },
      visibleLessons() { return this.view === 'agenda' ? this.lessons : this.lessons.filter(d => d.date === this.selectedDate); },
      selectedDayLabel() { return this.selectedDate ? Number(this.selectedDate.slice(-2)) + ' ' + this.selectedMonthText + ' dersleri' : 'Günün dersleri'; },
      nextLessonKey() {
        const next = this.lessons.find(d => d.lessonStatus === 0 && !d.isAbsenceDeclared && (d.timestamp ? d.timestamp >= Date.now() : d.date >= this.today));
        return next ? next.key : null;
      },
      dayShortTextArr : ()=> ['PAZ', 'PZT', 'SAL', 'ÇAR', 'PER', 'CUM', 'CMT'],
      monthTextArr:()=> ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'],
      postponeDialogContent:function(){
          return this.postponeDialog.constContent.replace('#LESSONDATE#',DateFormat(this.postponeDialog.lessonDate,'dd.mm.yyyy'));
      },
    },
    watch: {
      view(value) {
        if (value === 'agenda') this.scrollToCurrentLesson();
      }
    },
    methods: {
      async scrollToCurrentLesson() {
        if (!this.autoScrollToLesson || this.view !== 'agenda' || this.loading || this.loadError) return;
        const target = this.lessons.find(d => d.date === this.today)
          || this.lessons.find(d => d.key === this.nextLessonKey);
        if (!target) return;
        await this.$nextTick();
        if (this.view !== 'agenda' || this.loading) return;
        const card = Array.from(this.$refs.agendaList?.children || [])
          .find(element => element.dataset.lessonKey === target.key);
        card?.scrollIntoView({ block: 'start', behavior: 'instant' });
      },
      ...mapActions(["GetTrainingSchedule","PostponeLesson","DeclareAbsence"]),
      OpenCloseAbsenceDialogModal(visible,lesson) {
        if (lesson != null) {
          this.absenceDialog.lesson = lesson;
        }
        this.absenceDialog.visible = visible;
      },
      AbsenceDialogAnswer(answer) {
        if (answer) {
          this.DeclareAbsenceProcess();
        }
      },
      async DeclareAbsenceProcess() {
        let lesson = this.absenceDialog.lesson;
        this.loadingButtonDisable = true;
        let toastId = LoadingToast();
        await this.DeclareAbsence(lesson.studentScheduleId)
          .then(async (res) => {
            UpdateToast(toastId,res.description,ToastType().SUCCESS);
            lesson.isAbsenceDeclared = true;
            lesson.canDeclareAbsence = false;
            this.loadingButtonDisable = false;
          })
          .catch((err) => {
            UpdateToast(toastId,err.message,ToastType().ERROR);
            this.loadingButtonDisable = false;
          });
      },
      OpenClosePostponeDialogModal(visible,p){
        if(p!=null){
          this.postponeDialog.lessonDate = p.scheduleDate;
          this.postponeDialog.trainingFileId = p.trainingFileId;
        }
        this.postponeDialog.postponeDialogModalVisible=visible;
      },
      PostponeDialogAnswer(answer){
        if(answer){
          this.PostponeLessonProcess();
        }
      },
      async PostponeLessonProcess() {
          this.loadingButtonDisable=true;
          let toastId = LoadingToast();
          let model = {
              trainingFileId: this.postponeDialog.trainingFileId,
              lessonDate: this.postponeDialog.lessonDate
          }
          await this.PostponeLesson(model)
          .then(async (res) => {
            UpdateToast(toastId,res.description,ToastType().SUCCESS);
            await this.init();
            this.loadingButtonDisable=false;
          })
          .catch((err) => {
            UpdateToast(toastId,err.message,ToastType().ERROR);
            this.loadingButtonDisable=false;
          });
      },
      CreateUrl(value){
        return CleanUrl(value);
      },
      dateKey(value) {
        return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit' }).format(value);
      },
      statusLabel(status) { return ({ 0: 'Planlandı', 1: 'Katılım olmadı', 2: 'Tamamlandı', 3: 'Ertelendi' })[status] || 'Ders'; },
      resources(d) {
        return [ ['hasWord', 'bi bi-file-word-fill', 'Kelime çalışması'], ['hasAudio', 'bi bi-headphones', 'Ses kaydı'], ['hasVideo', 'bi bi-play-btn', 'Video'], ['hasTest', 'bi bi-stopwatch-fill', 'Test'], ['hasLessonNote', 'bi bi-journal-bookmark-fill', 'Ders notları'] ]
          .filter(([field]) => d[field]).map(([, icon, label]) => ({ icon, label }));
      },
      async init() {
        this.loading = true;
        this.loadError = false;
        this.lessons = [];
        this.dayInMonth = [];
        this.selectedMonthText = this.monthTextArr[this.selectedMonth];
        const month = this.selectedYear + '-' + String(this.selectedMonth + 1).padStart(2, '0');
        try {
          const schedule = await this.GetTrainingSchedule({ date: month + '-01' });
          if (!Array.isArray(schedule)) throw new Error('Invalid schedule');
          this.lessons = schedule.map((lesson, index) => {
            const book = lesson.book || {};
            const instant = lesson.lessonDateTime ? new Date(lesson.lessonDateTime) : null;
            const validTime = instant && Number.isFinite(instant.getTime()) && instant.getFullYear() > 2000;
            const date = validTime ? this.dateKey(instant) : String(lesson.date).slice(0, 10);
            const localDay = new Date(date + 'T12:00:00');
            return {
              ...lesson, key: String(lesson.studentScheduleId || lesson.trainingFileId || '') + '-' + index,
              date, scheduleDate: String(lesson.date).slice(0, 10), dayNumber: Number(date.slice(-2)), dayText: this.dayShortTextArr[localDay.getDay()],
              timestamp: validTime ? instant.getTime() : null,
              time: validTime ? new Intl.DateTimeFormat('tr-TR', { timeZone: 'Europe/Istanbul', hour: '2-digit', minute: '2-digit' }).format(instant) : '',
              lessonStatus: Number(lesson.status), bookTitle: book.bookName || '', bookLevel: book.bookLevel || '',
              unitNumber: book.unitNumber ? 'Ünite ' + book.unitNumber + ':' : '', subjectTitle: book.subject || '',
              hasWord: book.hasWord, hasAudio: book.hasAudio, hasVideo: book.hasVideo, hasTest: book.hasTest
            };
          }).filter(d => d.date.startsWith(month + '-') && d.lessonStatus >= 0)
            .sort((a, b) => a.date.localeCompare(b.date) || (a.timestamp || 0) - (b.timestamp || 0));
          const days = new Date(this.selectedYear, this.selectedMonth + 1, 0).getDate();
          this.dayInMonth = Array.from({ length: days }, (_, index) => {
            const date = month + '-' + String(index + 1).padStart(2, '0');
            return { date, dayNumber: index + 1, lessons: this.lessons.filter(d => d.date === date) };
          });
          if (!this.selectedDate.startsWith(month + '-')) {
            this.selectedDate = this.today.startsWith(month + '-') ? this.today : this.lessons[0]?.date || month + '-01';
          }
        } catch {
          this.loadError = true;
        } finally {
          this.loading = false;
        }
      },
      nextMonth() {
          if (this.busy) return;
          let month = this.selectedMonth + 1;
          if (month > 11) {
              this.selectedMonth = 0;
              this.selectedYear++;
          } else {
              this.selectedMonth = month;
          }
          this.init();
      },
      prevMonth() {
          if (this.busy) return;
          let month = this.selectedMonth - 1;
          if (month < 0) {
              this.selectedMonth = 11;
              this.selectedYear--;
          } else {
              this.selectedMonth = month;
          }
          this.init();
      },
      refreshMonth() {
          this.init();
      },
    },
    mounted: async function () {
      await this.init();
      await this.scrollToCurrentLesson();
    }
  };
  </script>
    
  <style scoped>
.schedule-card { padding: 32px; background: #fff; border: 1px solid #e2e8f0cc; border-radius: 24px; box-shadow: 0 1px 2px #0f172a0d; }
.schedule-toolbar { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 20px; padding-bottom: 24px; margin-bottom: 24px; border-bottom: 1px solid #f1f5f9; }
h1 { margin: 0; font-size: 24px; font-weight: 700; letter-spacing: -.03em; color: #0f172a; }
.schedule-toolbar p { margin: 6px 0 0; color: #475569; font-size: 12px; }
.month-switcher { display: flex; align-items: center; gap: 12px; padding: 6px 12px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 16px; }
.month-switcher span { min-width: 100px; text-align: center; font-size: 14px; font-weight: 600; }
.month-switcher button { border: 0; background: transparent; padding: 6px; border-radius: 12px; color: #475569; }
.month-switcher button:hover { background: #fff; color: #0f172a; }
.view-switcher { display: flex; padding: 4px; background: #f1f5f9; border: 1px solid #e2e8f099; border-radius: 12px; }
.view-switcher button { padding: 8px 12px; border: 0; border-radius: 8px; background: transparent; color: #475569; font-size: 12px; }
.view-switcher button[aria-pressed="true"] { background: white; color: #0f172a; font-weight: 600; box-shadow: 0 1px 2px #0f172a0d; }
.agenda-list { display: grid; gap: 14px; }
.lesson-card { display: flex; align-items: center; gap: 16px; padding: 20px; border: 1px solid #e2e8f0cc; border-radius: 16px; background: #fff; transition: border-color .15s, box-shadow .15s; }
.lesson-card:hover { border-color: #cbd5e1; box-shadow: 0 1px 2px #0f172a0d; }
.lesson-card.is-today { background: #ecfdf54d; border-color: #6ee7b7; box-shadow: 0 0 0 1px #34d39933; }
.lesson-card.is-completed { background: #f8fafc99; border-color: #e2e8f099; }
.date-badge { display: flex; flex-direction: column; align-items: center; justify-content: center; width: 56px; height: 56px; flex-shrink: 0; border-radius: 12px; background: #f1f5f9; color: #334155; }
.date-badge strong { font-size: 20px; line-height: 1; }.date-badge span { margin-top: 4px; font-size: 10px; font-weight: 500; text-transform: uppercase; letter-spacing: .07em; }
.lesson-card.is-today .date-badge { background: var(--brand-primary); color: #fff; }
.lesson-info { flex: 1; min-width: 0; }.lesson-tags { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; color: #475569; font-size: 12px; }
.status { padding: 3px 8px; border-radius: 6px; background: #f1f5f9; color: #475569; font-size: 11px; font-weight: 600; }.status-next { background: #d1fae5; color: #065f46; }
h3 { margin: 8px 0; color: #0f172a; font-size: 16px; font-weight: 700; line-height: 1.5; overflow-wrap: anywhere; }.lesson-meta { display: flex; flex-wrap: wrap; gap: 12px; font-size: 12px; color: #475569; }.lesson-meta i { margin-right: 4px; }
.lesson-actions { display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-end; gap: 8px; max-width: 230px; }
.action { display: inline-flex; align-items: center; justify-content: center; gap: 6px; padding: 10px 16px; border: 1px solid transparent; border-radius: 12px; font-size: 12px; font-weight: 600; text-decoration: none; transition: background .15s, transform .15s; }
.primary { background: var(--brand-primary); color: white; }.primary:hover { background: var(--brand-primary-hover); color: white; }.primary:active { background: #065f46; transform: scale(.95); }.secondary { background: white; border-color: #e2e8f0; color: #334155; }.secondary:hover { background: #f8fafc; color: #0f172a; }
.absence, .absence-notice { background: #fffbeb; color: #92400e; border: 1px solid #fde68a; }.absence:hover { background: #fef3c7; }.absence-notice { padding: 6px 10px; border-radius: 8px; font-size: 11px; }
.empty-state { text-align: center; padding: 64px 24px; border: 2px dashed #e2e8f0; border-radius: 24px; }.empty-state > i { color: #94a3b8; font-size: 32px; }.empty-state h2 { margin: 16px 0 8px; font-size: 18px; }.empty-state p { color: #475569; font-size: 13px; }.empty-state .action { margin-top: 16px; }.empty-state.compact { padding: 24px; }.compact p { margin: 0; }
.calendar { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 8px; margin-bottom: 24px; }.weekday { padding: 8px 0; text-align: center; font-size: 12px; color: #475569; }.calendar-day { min-height: 78px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; border: 1px solid #e2e8f0; border-radius: 12px; background: white; color: #475569; }.calendar-day.has-lessons { background: #ecfdf5; color: #065f46; }.calendar-day.is-today > span { text-decoration: underline; text-underline-offset: 4px; font-weight: 700; }.calendar-day[aria-pressed="true"] { border-color: var(--brand-primary); box-shadow: 0 0 0 1px var(--brand-primary); }.calendar-day:hover { border-color: var(--brand-primary); }.calendar-day small { font-size: 10px; }.day-heading { font-size: 16px; font-weight: 600; margin: 0 0 16px; }
.schedule-loading { display: grid; gap: 14px; }.skeleton { height: 112px; border-radius: 16px; background: #e2e8f0; animation: schedule-pulse 1.5s ease-in-out infinite; }
button:disabled { background: #f1f5f9; color: #94a3b8; cursor: not-allowed; transform: none; }button:focus-visible, a:focus-visible { outline: 3px solid var(--brand-primary-hover); outline-offset: 3px; }
@keyframes schedule-pulse { 50% { opacity: .45; } }
@media (max-width: 767px) { .schedule-card { padding: 24px; }.lesson-card { flex-wrap: wrap; }.lesson-info { flex-basis: calc(100% - 72px); }.lesson-actions { max-width: none; width: 100%; justify-content: flex-start; } }
@media (max-width: 479px) { .schedule-card { padding: 20px 16px; }.schedule-toolbar { align-items: stretch; }.schedule-toolbar > div { width: 100%; }.month-switcher { justify-content: space-between; }.view-switcher button { flex: 1; }.lesson-card { padding: 16px; gap: 12px; }.calendar { gap: 4px; }.calendar-day { min-height: 58px; border-radius: 8px; padding: 4px 0; }.calendar-day small { font-size: 9px; }.empty-state { padding: 40px 16px; } }
@media (prefers-reduced-motion: reduce) { *, *::before { animation: none !important; transition: none !important; } }
</style>
